// Copyright (c) 2026 Watch Tower Bible and Tract Society of Pennsylvania
// All Rights Reserved. Proprietary and confidential.
// Unauthorized copying, modification, or distribution is strictly prohibited.
import { useState, useEffect, useCallback, useRef } from 'react';
import { PDFDocument } from 'pdf-lib';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  FileText,
  Save,
  Loader2,
  CheckSquare,
  Type,
  List,
  AlertCircle,
  ChevronDown,
  ClipboardList,
} from 'lucide-react';
import { S25B_CONFIG, S45B_CONFIG, S26_CONFIG, S44_CONFIG } from '../form-definitions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Detecta o tipo de formulário pelo nome do arquivo para usar o mapa correto.
function detectFormConfig(filename) {
  const n = (filename || '').toLowerCase();
  if (n.includes('s-45') || n.includes('s45')) return S45B_CONFIG;
  if (n.includes('s-25') || n.includes('s25')) return S25B_CONFIG;
  if (n.includes('s-26') || n.includes('s26')) return S26_CONFIG;
  if (n.includes('s-44') || n.includes('s44')) return S44_CONFIG;
  return null;
}

function getFieldLabel(config, fieldName) {
  if (config?.fieldMap[fieldName]) return config.fieldMap[fieldName].label;
  // Fallback: clean up technical name
  return fieldName
    .replace(/^900_/, '')
    .replace(/_Text_C$/, '')
    .replace(/_Text$/, '')
    .replace(/_/g, ' ');
}

function getFieldSection(config, fieldName) {
  return config?.fieldMap[fieldName]?.section || null;
}

export default function PdfFormFiller({ file, open, onOpenChange, onSaved }) {
  const [fields, setFields] = useState([]);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pdfBytes, setPdfBytes] = useState(null);
  const [error, setError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [activeSection, setActiveSection] = useState(null);

  // Ref sempre aponta para a URL de blob mais recente, evitando revogar a URL errada no cleanup.
  const previewUrlRef = useRef(null);
  useEffect(() => { previewUrlRef.current = previewUrl; }, [previewUrl]);

  useEffect(() => {
    if (file && open) {
      loadPdfFields();
    }
    return () => {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
      setFields([]);
      setValues({});
      setPdfBytes(null);
      setError(null);
      setPreviewUrl(null);
      setActiveSection(null);
    };
    // loadPdfFields is recreated every render; re-run only when file/open change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, open]);

  const loadPdfFields = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(`${API}/files/${file.id}/download`, {
        responseType: 'arraybuffer',
      });

      const bytes = new Uint8Array(response.data);
      setPdfBytes(bytes);

      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      setPreviewUrl(url);

      const pdfDoc = await PDFDocument.load(bytes, { ignoreEncryption: true });
      const form = pdfDoc.getForm();
      const pdfFields = form.getFields();

      if (pdfFields.length === 0) {
        setError('Este PDF não possui campos de formulário para preencher.');
        setLoading(false);
        return;
      }

      const extractedFields = pdfFields.map((field) => {
        const name = field.getName();
        const type = field.constructor.name;
        const formConfig = detectFormConfig(file?.original_filename);

        let fieldInfo = { name, type, options: [], label: getFieldLabel(formConfig, name), section: getFieldSection(formConfig, name) };

        if (type === 'PDFTextField') {
          fieldInfo.currentValue = field.getText() || '';
          fieldInfo.isMultiline = field.isMultiline();
        } else if (type === 'PDFCheckBox') {
          fieldInfo.currentValue = field.isChecked();
        } else if (type === 'PDFDropdown') {
          fieldInfo.options = field.getOptions();
          fieldInfo.currentValue = field.getSelected()?.[0] || '';
        } else if (type === 'PDFRadioGroup') {
          fieldInfo.options = field.getOptions();
          fieldInfo.currentValue = field.getSelected() || '';
        } else if (type === 'PDFOptionList') {
          fieldInfo.options = field.getOptions();
          fieldInfo.currentValue = field.getSelected()?.[0] || '';
        }

        return fieldInfo;
      });

      setFields(extractedFields);

      const initialValues = {};
      extractedFields.forEach((f) => {
        initialValues[f.name] = f.currentValue ?? '';
      });
      setValues(initialValues);

      // Set first section as active
      const firstSection = extractedFields.find(f => f.section)?.section;
      if (firstSection) setActiveSection(firstSection);
    } catch (err) {
      console.error('Error loading PDF:', err);
      setError('Erro ao carregar o PDF. O arquivo pode estar protegido ou corrompido.');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = useCallback((fieldName, value) => {
    setValues((prev) => ({ ...prev, [fieldName]: value }));
  }, []);

  const handleSave = async () => {
    if (!pdfBytes) return;
    setSaving(true);

    try {
      const pdfDoc = await PDFDocument.load(pdfBytes, { ignoreEncryption: true });
      const form = pdfDoc.getForm();

      fields.forEach((field) => {
        try {
          const val = values[field.name];
          if (field.type === 'PDFTextField') {
            form.getTextField(field.name).setText(String(val || ''));
          } else if (field.type === 'PDFCheckBox') {
            const cb = form.getCheckBox(field.name);
            val ? cb.check() : cb.uncheck();
          } else if (field.type === 'PDFDropdown') {
            if (val) form.getDropdown(field.name).select(val);
          } else if (field.type === 'PDFRadioGroup') {
            if (val) form.getRadioGroup(field.name).select(val);
          } else if (field.type === 'PDFOptionList') {
            if (val) form.getOptionList(field.name).select(val);
          }
        } catch (e) {
          console.warn(`Could not set field ${field.name}:`, e);
        }
      });

      const filledBytes = await pdfDoc.save();

      const blob = new Blob([filledBytes], { type: 'application/pdf' });
      const formData = new FormData();
      formData.append('file', blob, file.original_filename);

      await axios.put(`${API}/files/${file.id}/update-content`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (onSaved) onSaved();
      onOpenChange(false);
    } catch (err) {
      console.error('Error saving PDF:', err);
      setError('Erro ao salvar o formulário. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  // Group fields by section
  const sections = [];
  const sectionMap = new Map();
  fields.forEach((field) => {
    const sec = field.section || 'Outros Campos';
    if (!sectionMap.has(sec)) {
      sectionMap.set(sec, []);
      sections.push(sec);
    }
    sectionMap.get(sec).push(field);
  });

  // Count filled fields per section
  const getSectionProgress = (sectionName) => {
    const sectionFields = sectionMap.get(sectionName) || [];
    const filled = sectionFields.filter(f => {
      const v = values[f.name];
      if (typeof v === 'boolean') return true;
      return v && String(v).trim() !== '';
    }).length;
    return { filled, total: sectionFields.length };
  };

  const getFieldIcon = (type) => {
    switch (type) {
      case 'PDFTextField': return Type;
      case 'PDFCheckBox': return CheckSquare;
      case 'PDFDropdown':
      case 'PDFOptionList': return List;
      default: return FileText;
    }
  };

  if (!file) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-6xl max-h-[92vh] overflow-hidden flex flex-col"
        data-testid="pdf-form-filler-modal"
      >
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
            <ClipboardList className="w-5 h-5 text-red-500" />
            Preencher Formulário - {file.original_filename}
          </DialogTitle>
          <DialogDescription>
            Preencha os campos por seção. Seu progresso é salvo ao clicar em Salvar.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col md:flex-row gap-3 py-2 min-h-0">
          {/* PDF Preview */}
          <div className="md:w-[45%] flex-shrink-0 overflow-hidden rounded-lg border border-border">
            {previewUrl ? (
              <iframe
                src={`${previewUrl}#toolbar=0&navpanes=0`}
                className="w-full h-[25vh] md:h-full"
                title="PDF Preview"
              />
            ) : (
              <div className="flex items-center justify-center h-[25vh] md:h-full bg-muted">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            )}
          </div>

          {/* Form Fields */}
          <div className="md:w-[55%] overflow-hidden flex flex-col min-h-0" data-testid="pdf-form-fields">
            {loading ? (
              <div className="flex flex-col items-center justify-center h-48">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground mb-3" />
                <p className="text-sm text-muted-foreground">Analisando formulário...</p>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center h-48 text-center px-4">
                <AlertCircle className="w-10 h-10 text-amber-500 mb-3" />
                <p className="text-sm text-muted-foreground">{error}</p>
              </div>
            ) : fields.length > 0 ? (
              <>
                {/* Section tabs */}
                <div className="flex gap-1 overflow-x-auto pb-2 flex-shrink-0 scrollbar-thin">
                  {sections.map((sec) => {
                    const { filled, total } = getSectionProgress(sec);
                    const color = detectFormConfig(file?.original_filename)?.sectionColors?.[sec] || 'bg-slate-500';
                    return (
                      <button
                        key={sec}
                        onClick={() => setActiveSection(sec)}
                        data-testid={`section-tab-${sec}`}
                        className={`
                          flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium transition-all
                          ${activeSection === sec
                            ? 'bg-primary text-primary-foreground shadow-sm'
                            : 'bg-muted text-muted-foreground hover:bg-accent'
                          }
                        `}
                      >
                        <span className="flex items-center gap-1.5">
                          <span className={`w-2 h-2 rounded-full ${color}`} />
                          {sec}
                          <span className="opacity-60">({filled}/{total})</span>
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* Fields for active section */}
                <div className="flex-1 overflow-y-auto space-y-2 pr-1 mt-2">
                  {(sectionMap.get(activeSection) || []).map((field) => {
                    const Icon = getFieldIcon(field.type);
                    const hasValue = (() => {
                      const v = values[field.name];
                      if (typeof v === 'boolean') return true;
                      return v && String(v).trim() !== '';
                    })();

                    return (
                      <div
                        key={field.name}
                        className={`p-3 rounded-lg border transition-colors ${
                          hasValue ? 'border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-950/20' : 'border-border bg-card'
                        }`}
                        data-testid={`form-field-${field.name}`}
                      >
                        <div className="flex items-start gap-2 mb-1.5">
                          <Icon className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                          <Label className="text-sm font-medium leading-snug">{field.label}</Label>
                        </div>

                        {field.type === 'PDFTextField' && (
                          field.isMultiline ? (
                            <textarea
                              className="w-full px-3 py-2 text-sm rounded-md border border-input bg-background text-foreground resize-y min-h-[60px]"
                              value={values[field.name] || ''}
                              onChange={(e) => handleFieldChange(field.name, e.target.value)}
                              placeholder="Digite aqui..."
                              data-testid={`input-${field.name}`}
                            />
                          ) : (
                            <Input
                              value={values[field.name] || ''}
                              onChange={(e) => handleFieldChange(field.name, e.target.value)}
                              placeholder="Digite aqui..."
                              className="text-sm"
                              data-testid={`input-${field.name}`}
                            />
                          )
                        )}

                        {field.type === 'PDFCheckBox' && (
                          <label className="flex items-center gap-2 cursor-pointer" data-testid={`input-${field.name}`}>
                            <input
                              type="checkbox"
                              checked={!!values[field.name]}
                              onChange={(e) => handleFieldChange(field.name, e.target.checked)}
                              className="w-4 h-4 rounded border-input"
                            />
                            <span className="text-sm text-foreground">Marcado</span>
                          </label>
                        )}

                        {(field.type === 'PDFDropdown' || field.type === 'PDFOptionList') && (
                          <div className="relative">
                            <select
                              value={values[field.name] || ''}
                              onChange={(e) => handleFieldChange(field.name, e.target.value)}
                              className="w-full px-3 py-2 text-sm rounded-md border border-input bg-background text-foreground appearance-none pr-8"
                              data-testid={`input-${field.name}`}
                            >
                              <option value="">Selecione...</option>
                              {field.options.map((opt) => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </select>
                            <ChevronDown className="w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                          </div>
                        )}

                        {field.type === 'PDFRadioGroup' && (
                          <div className="space-y-1" data-testid={`input-${field.name}`}>
                            {field.options.map((opt) => (
                              <label key={opt} className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="radio"
                                  name={field.name}
                                  value={opt}
                                  checked={values[field.name] === opt}
                                  onChange={(e) => handleFieldChange(field.name, e.target.value)}
                                  className="w-4 h-4"
                                />
                                <span className="text-sm text-foreground">{opt}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            ) : null}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between pt-3 border-t border-border flex-shrink-0">
          <p className="text-xs text-muted-foreground">
            {fields.length > 0 && (() => {
              const filled = fields.filter(f => {
                const v = values[f.name];
                if (typeof v === 'boolean') return true;
                return v && String(v).trim() !== '';
              }).length;
              return `${filled} de ${fields.length} campos preenchidos`;
            })()}
          </p>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="cancel-form-fill"
            >
              Cancelar
            </Button>
            {fields.length > 0 && !error && (
              <Button
                onClick={handleSave}
                disabled={saving}
                data-testid="save-form-fill"
              >
                {saving ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Salvando...</>
                ) : (
                  <><Save className="w-4 h-4 mr-2" /> Salvar Formulário</>
                )}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
